/**
 * Ce qu'un geste sur la grille de mesures produit une fois relâché.
 *
 * La règle la plus fragile de cet écran est la distinction entre le tap et le
 * cerné : les deux arrivent par le même geste de pointeur, à quelques pixels
 * près, et rien dans le DOM ne les sépare. Un tap sélectionne une mesure *et*
 * s'y rend ; un cerné pose une plage sans déplacer la tête de lecture. La
 * confondre, c'est faire sauter la lecture au moindre glissé, ou rendre toute
 * boucle d'une mesure impossible à produire. D'où cette fonction pure, seule
 * dépositaire de la règle et vérifiable sans navigateur.
 */

import type { LoopRange } from "../../molecules/measure-grid";

export type MeasureSelection =
  /** Aucun geste en cours : rien à valider. */
  | { readonly kind: "none" }
  /** Le cerné retombe exactement sur la plage déjà posée : on l'efface. */
  | { readonly kind: "clear" }
  /** Un cerné : on pose la plage, sans déplacer la lecture. */
  | { readonly kind: "range"; readonly loop: LoopRange }
  /** Un tap : on sélectionne la mesure et on s'y rend. */
  | { readonly kind: "tap"; readonly loop: LoopRange };

/**
 * @param start   Mesure sous le pointeur à l'appui, `null` hors geste.
 * @param end     Mesure sous le pointeur au relâchement.
 * @param dragged Le pointeur a-t-il quitté sa mesure de départ ? C'est cela, et
 *                non `start !== end`, qui fait le cerné : un aller-retour qui
 *                revient sur la mesure d'appui reste une plage d'une mesure, et
 *                ne doit donc pas déplacer la lecture comme le ferait un tap.
 * @param loop    Plage actuellement sélectionnée.
 */
export function resolveMeasureSelection(
  start: number | null,
  end: number | null,
  dragged: boolean,
  loop: LoopRange | null,
): MeasureSelection {
  if (start === null || end === null) {
    return { kind: "none" };
  }

  if (!dragged) {
    return { kind: "tap", loop: { startMeasure: start, endMeasure: start } };
  }

  const startMeasure = Math.min(start, end);
  const endMeasure = Math.max(start, end);

  if (
    loop !== null &&
    loop.startMeasure === startMeasure &&
    loop.endMeasure === endMeasure
  ) {
    return { kind: "clear" };
  }

  return { kind: "range", loop: { startMeasure, endMeasure } };
}
